import { Page, Div, Button, router, state, IRouteEvent } from '@codesuma/baseline'
import { PageHead } from '../../components/page-head'
import { Slides, WizardNav } from '../../components/wizard'
import calendar from '../../services/calendar'
import settings from '../../services/settings'
import rating from '../../services/rating'
import { dayLine } from '../../helpers/day-line'
import { paleYear } from '../../helpers/year'
import { EntryKind } from './draft'
import { SlideId, canAdvance, canSave, nextLabel, step, visibleSlides } from './flow'
import { buildSlide } from './editor-steps'
import { loadSession } from './session'
import { removeEntry, saveEntry } from './persistence'
import style from './index.module.css'

// One editor for new entries and existing events and occasions.
// /occasion remains an alias with the occasion type preselected.
export const EventPage = (defaultKind: EntryKind = 'event') => {
    const base = Page()
    base.addClass(style.base)
    base.style({ background: 'var(--paper)', color: 'var(--ink)',
        padding: 'calc(var(--safe-top) + 1rem) var(--gutter) calc(var(--safe-bottom) + 1rem)' })
    let session = loadSession({}, defaultKind)
    let current: SlideId = 'title'
    let busy = false
    let armed = false
    let visit = 0
    // The app's one page head, with the line under it the editors carry.
    // It refuses to go back mid-save, which is this page's own rule.
    const head = PageHead(undefined, { rule: true, onDismiss: () => { if (!busy) router.back() } })
    const stage = Slides()
    const nav = WizardNav()
    const note = Div().addClass(style.note)
    const error = Div().addClass(style.hint)
    error.attr('role', 'alert')
    const remove = Button().addClass(style.remove)
    base.append(head, stage, nav, note, error, remove)

    const ids = () => visibleSlides(session.draft, session.pickDay, session.pickKind)
    const syncDate = () => {
        if (!session.original) session.day = calendar.dayAt(session.day.gy, session.day.gm, session.day.gd, 0)
        head.setDate(paleYear(dayLine(session.day, settings.get().system)))
    }
    const renderNav = () => {
        const steps = ids()
        const index = steps.indexOf(current)
        const last = index === steps.length - 1
        nav.setState({ steps: steps.length, index,
            nextLabel: busy ? 'در حال ذخیره…' : nextLabel(current, steps),
            nextEnabled: !busy && canAdvance(current, session.draft), showPrev: index > 0 && !busy,
            // Not on the last step, where ادامه already is ذخیره.
            showDone: !busy && !last && canSave(session.draft, steps) })
        remove.style({ display: session.editing ? '' : 'none' })
        remove.attr('aria-disabled', String(busy))
        head.dismiss.attr('aria-disabled', String(busy))
        syncDate()
    }
    const disarm = () => {
        armed = false
        remove.text('حذف رویداد')
        remove.removeClass(style.removeArmed)
    }
    const goto = (id: SlideId, direction: 'forward' | 'back' | 'none') => {
        current = id
        const slide = buildSlide(id, session)
        slide.on('change', () => { disarm(); renderNav() })
        slide.on('advance', advance)
        stage.show(slide, direction)
        error.text('')
        renderNav()
        disarm()
        const revision = visit
        if (slide.focus) setTimeout(() => {
            if (revision === visit && current === id) slide.focus!()
        }, 220)
    }
    async function perform(action: 'save' | 'remove') {
        if (busy) return
        busy = true
        error.text('')
        renderNav()
        try {
            // A new entry saved is a good moment to ask for a rating; an edit is not.
            const added = action === 'save' && !session.editing
            if (action === 'save') await saveEntry(session)
            else await removeEntry(session)
            router.back()
            if (added) rating.goodMoment()
        } catch (e) {
            error.text(e instanceof Error ? e.message : 'ذخیره انجام نشد؛ دوباره تلاش کنید.')
        } finally {
            busy = false
            renderNav()
        }
    }
    async function advance() {
        if (busy || !canAdvance(current, session.draft)) return
        const next = step(current, ids(), 1)
        if (next) goto(next, 'forward')
        else await perform('save')
    }
    nav.on('next', advance)
    // Guarded as well as hidden: the shortcut is only drawn once nothing in the
    // flow is blocking, and it must not be able to save past a step that is.
    nav.on('done', () => {
        if (busy || !canSave(session.draft, ids())) return
        void perform('save')
    })
    nav.on('prev', () => {
        if (busy) return
        const previous = step(current, ids(), -1)
        if (previous) goto(previous, 'back')
    })
    remove.on('click', () => {
        if (busy) return
        if (!armed) {
            armed = true
            remove.text('برای حذف دوباره بزنید')
            remove.addClass(style.removeArmed)
        } else void perform('remove')
    })
    base.on('enter', ({ query }: IRouteEvent) => {
        visit++
        const q = (query || {}) as Record<string, string>
        session = loadSession(q, defaultKind)
        busy = false
        error.text('')
        head.setTitle(session.editing ? 'ویرایش رویداد' : 'رویداد تازه')
        note.empty()
        const missing = !!(q.id && !session.editing)
        note.style({ display: missing ? '' : 'none' })
        stage.style({ display: missing ? 'none' : '' })
        nav.style({ display: missing ? 'none' : '' })
        remove.style({ display: 'none' })
        syncDate()
        if (missing) {
            note.append(Div('این مورد دیگر در دسترس نیست.'))
            return
        }
        goto(ids()[0], 'none')
    })
    base.on('exit', () => { visit++ })
    state.on('hijri', syncDate)
    state.on('settings', syncDate)
    base.on('unmounted', () => {
        visit++
        state.off('hijri', syncDate)
        state.off('settings', syncDate)
    })
    return base
}
